// Enqueue side of the PMS outbox. The only part of this layer that UI code
// touches, and the only part that talks to Amplify.
//
// Two rules hold everywhere in this file:
//   1. It never throws. The Bins write has already been committed by the time
//      a caller gets here; a vendor-sync problem must not surface as a failed
//      signup or a failed order completion.
//   2. It never sends anything to a vendor. Phase 1 builds the request, records
//      it, and stops. The drainer arrives in a later phase.

import { API } from 'aws-amplify';

import { createExternalRef, createPmsSyncJob } from '../../graphql/mutations';
import {
  buildAddUpdateAccount,
  buildProcessMoveIn,
  buildProcessPayment,
  createShadowTransport,
  getAdapter,
  getPmsConfig,
  jobId as buildJobId,
  paymentReservationId,
  queueKey,
  supports,
} from './core';
import type {
  EnsureTenantInput,
  MoveInInput,
  PmsFacilityConfig,
  PmsOp,
  PmsRequestEnvelope,
  RecordPaymentInput,
} from './core';
import { PmsGuardError } from './core';
import { consoleLogger, logShadowRequest } from './shadow';

export type EnqueueSkipReason =
  | 'no_provider' // this facility has no PMS integration
  | 'unsupported' // the vendor cannot do this operation (e.g. SSM move-out)
  | 'duplicate' // a job for this exact key already exists
  | 'guard' // our mapper produced an unsafe body — a bug, never queued
  | 'timeout' // took too long; the caller was released (see ENQUEUE_TIMEOUT_MS)
  | 'error'; // anything else; already logged

/**
 * Hard cap on how long a caller can wait for us.
 *
 * This runs on the customer-facing signup path, so it must never be the reason
 * someone is left staring at a spinner. On timeout the caller is released and
 * the underlying write is left to finish on its own — if it lands, the row is
 * still recorded; if it doesn't, we lose one shadow note, which costs nothing.
 */
const ENQUEUE_TIMEOUT_MS = 4000;

export interface EnqueueResult {
  enqueued: boolean;
  jobID: string;
  skipped?: EnqueueSkipReason;
}

export interface EnqueueInput {
  facilityID: string | null | undefined;
  op: PmsOp;
  /** Our id for the thing being synced; part of the idempotency key. */
  binsID: string;
  /** Canonical input for the operation (cents, ISO dates, our ids). */
  payload: EnsureTenantInput | MoveInInput | RecordPaymentInput;
  /** Overrides the derived idempotency key — payments pass `pay:<ledgerEntryID>`. */
  jobID?: string;
}

function isDuplicateError(error: unknown): boolean {
  const text = JSON.stringify(error == null ? '' : error);
  return /ConditionalCheckFailedException|already exists/i.test(text);
}

/** Pure request builders, by operation. Only SSM has a mapper today. */
function buildRequest(
  op: PmsOp,
  payload: EnqueueInput['payload'],
  config: PmsFacilityConfig,
): PmsRequestEnvelope | null {
  if (config.provider !== 'ssm' || !config.ssm) return null;

  switch (op) {
    case 'ensureTenant':
      return buildAddUpdateAccount(payload as EnsureTenantInput, config.ssm);
    case 'moveIn':
      return buildProcessMoveIn(payload as MoveInInput, config.ssm);
    case 'recordPayment':
      return buildProcessPayment(payload as RecordPaymentInput, config.ssm);
    default:
      return null;
  }
}

/**
 * Records one unit of vendor work. In shadow mode the row also carries the
 * exact body we would have sent, which is the whole point of the phase: the
 * payload gets reviewed against real customer data before anything is live.
 */
export async function enqueuePmsJob(input: EnqueueInput): Promise<EnqueueResult> {
  const nowISO = new Date().toISOString();
  const jobID = input.jobID || buildJobId(input.op, input.binsID, nowISO);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const releaseCaller = new Promise<EnqueueResult>((resolve) => {
    timer = setTimeout(
      () => resolve({ enqueued: false, jobID, skipped: 'timeout' }),
      ENQUEUE_TIMEOUT_MS,
    );
  });

  try {
    return await Promise.race([runEnqueue(input, jobID, nowISO), releaseCaller]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function runEnqueue(
  input: EnqueueInput,
  derivedJobID: string,
  nowISO: string,
): Promise<EnqueueResult> {
  const facilityID = (input.facilityID || '').trim();

  try {
    const config = getPmsConfig(facilityID);

    if (config.provider === 'none') {
      return { enqueued: false, jobID: derivedJobID, skipped: 'no_provider' };
    }

    // Capability check, never a provider check. The shadow transport is used
    // only so an adapter can be constructed; it performs no I/O.
    const adapter = getAdapter(config.provider, {
      transport: createShadowTransport(consoleLogger),
    });
    if (!supports(adapter, input.op)) {
      console.warn(`[pms] ${config.provider} cannot ${input.op}; needs a manual step in the PMS.`);
      return { enqueued: false, jobID: derivedJobID, skipped: 'unsupported' };
    }

    let request: PmsRequestEnvelope | null = null;
    try {
      request = buildRequest(input.op, input.payload, config);
    } catch (guardError) {
      if (guardError instanceof PmsGuardError) {
        // Our mapper built something unsafe. Do not queue it; this is a bug.
        console.error('[pms] refusing to queue an unsafe request', guardError.violations);
        return { enqueued: false, jobID: derivedJobID, skipped: 'guard' };
      }
      throw guardError;
    }

    const isShadowMode = config.mode !== 'live';
    const status = isShadowMode ? 'SHADOW' : 'PENDING';

    if (request && isShadowMode) {
      logShadowRequest(request);
    }

    await API.graphql({
      query: createPmsSyncJob,
      variables: {
        input: {
          id: derivedJobID,
          facilityID,
          provider: config.provider,
          op: input.op,
          binsID: input.binsID,
          mode: isShadowMode ? 'SHADOW' : 'LIVE',
          queueKey: queueKey(config.provider, status),
          payload: JSON.stringify(input.payload),
          request: request ? JSON.stringify(request.body) : null,
          status,
          attempts: 0,
          createdAtISO: nowISO,
          nextAttemptAtISO: nowISO,
        },
      },
    });

    return { enqueued: true, jobID: derivedJobID };
  } catch (error) {
    if (isDuplicateError(error)) {
      // Already recorded. Re-saving a ledger entry must not create a second
      // payment job, so this is a normal outcome, not a failure.
      return { enqueued: false, jobID: derivedJobID, skipped: 'duplicate' };
    }
    console.warn(`[pms] could not enqueue ${input.op} for ${input.binsID}:`, error);
    return { enqueued: false, jobID: derivedJobID, skipped: 'error' };
  }
}

/**
 * Claims the right to post one payment, before posting it.
 *
 * Amplify's generated create resolver writes with attribute_not_exists(id), so
 * if the row is already there this fails and we know the payment was already
 * posted (or is in flight). Unused in phase 1 — nothing posts yet — but it
 * belongs next to the enqueue it protects.
 */
export async function reservePayment(args: {
  facilityID: string;
  ledgerEntryID: string;
}): Promise<'reserved' | 'already' | 'error'> {
  const config = getPmsConfig(args.facilityID);
  if (config.provider === 'none') return 'error';

  try {
    await API.graphql({
      query: createExternalRef,
      variables: {
        input: {
          id: paymentReservationId(config.provider, args.ledgerEntryID, config.mode),
          facilityID: args.facilityID,
          provider: config.provider,
          entity: 'payment',
          binsID: args.ledgerEntryID,
          externalID: null, // filled in with the receipt once SSM accepts it
          shadow: config.mode !== 'live',
        },
      },
    });
    return 'reserved';
  } catch (error) {
    if (isDuplicateError(error)) return 'already';
    console.warn('[pms] payment reservation failed:', error);
    return 'error';
  }
}
